// src/App.jsx - Updated with shadcn sidebar integration
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SocketProvider } from './contexts/SocketContext.jsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.jsx';

// Auth components - using our new rebuilt components
import Login from './components/auth/Login';
import PartnerSignup from './components/auth/PartnerSignup';
import Register from './components/auth/Register';

// Dashboard component
import Dashboard from './components/dashboard/Dashboard.jsx';

// Dashboard components
import Appointments from './components/appointments/Appointments.jsx';
import AppointmentDetail from './components/appointments/AppointmentDetail';

// Customer components
import Customers from './components/Customers/Customers.jsx';
import CustomerDetails from './components/Customers/CustomerDetails.jsx';
import CustomerContent from './components/Customers/CustomerContent.jsx';

// Invoice components - UPDATED: Use shadcn invoices
import Invoices from './components/invoices/Invoices.jsx'; // NEW: Use shadcn component
import InvoiceBuilder from './components/invoices/InvoiceBuilder.jsx'; // NEW: Use shadcn form
import InvoiceDetail from './components/invoices/InvoiceDetail.jsx'; // Invoice detail view

// Other components
import PartnersSection from './components/partners/PartnersSection.jsx';

// Individual Configuration Section Components
import CommunicationsEditor from './components/configuration/sections/communications/CommunicationsEditor';
import ServicesSection from './components/configuration/sections/ServicesSection';
import TeamSection from './components/configuration/sections/TeamSection';

// Business Settings Page
import BusinessSettings from './pages/business/BusinessSettings';

// Account Settings
import AccountSettings from './pages/AccountSettings';

// Onboarding
import Onboarding from './pages/Onboarding';

// Guards
import OnboardingGuard from './guards/OnboardingGuard';

// Reviews components
import Reviews from './components/reviews/Reviews';

// Calendar Components
import Calendar from './components/calendar/Calendar.jsx';

import WidgetBuilder from './components/widgets/WidgetBuilder';
import SiteEditor from './components/site-editor/SiteEditor';

import InvitationAcceptance from './pages/InvitationAcceptance';
import InvoicePayment from './pages/public/InvoicePayment';

// Workflow/Automation Components
import { WorkflowCanvas } from './components/workflows';

// UI Components - UPDATED: Use shadcn sidebar
import Sidebar from './components/ui/navigation/Sidebar/Sidebar';
import { Toaster } from './components/ui/toaster';
import { Spinner } from './components/ui/spinner';

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, sessionExpired } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center justify-center">
          <Spinner className="size-16 text-primary mb-4" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Session expired modal is rendered globally by AuthContext — don't redirect,
  // let the user dismiss the modal and navigate themselves.
  if (sessionExpired) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// UPDATED: shadcn sidebar layout with proper SidebarInset
const AppLayout = ({ children }) => (
  <Sidebar>
    {children}
  </Sidebar>
);

function App() {
  React.useEffect(() => {
    // Only apply dark mode for dashboard routes, not site preview routes
    const isSitePreview = window.location.pathname.startsWith('/sites/') &&
                          window.location.pathname.includes('/preview');

    if (!isSitePreview) {
      document.documentElement.classList.add('dark');
    } else {
      // Ensure dark mode is removed for site previews
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <Router>
      <SocketProvider>
        <AuthProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/accept-invitation" element={<InvitationAcceptance />} />
            <Route path="/pay/:token" element={<InvoicePayment />} />
            <Route path="/login" element={<Login />} />
            <Route path="/partner-signup" element={<PartnerSignup />} />
            <Route path="/register" element={<Register />} />

            {/* Onboarding route - protected but not onboarding-guarded */}
            <Route path="/onboarding" element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            } />

            {/* Protected routes with onboarding guard */}
            <Route path="/" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />
            
            {/* Customer routes */}
            <Route path="/customers" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <Customers />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            <Route path="/customers/:customerId" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <CustomerDetails />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            <Route path="/customers/:customerId/content" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <CustomerContent />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            {/* Appointment routes */}
            <Route path="/appointments" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <Appointments />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            <Route path="/appointments/:appointmentId" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <AppointmentDetail />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            {/* Calendar route */}
            <Route path="/calendar" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <Calendar />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />
            
            {/* Invoice routes - CORRECTED */}
            <Route path="/invoices" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <Invoices />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            <Route path="/invoices/create" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <InvoiceBuilder />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            <Route path="/invoices/edit/:invoiceId" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <InvoiceBuilder />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            <Route path="/invoices/:invoiceId" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <InvoiceDetail />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            {/* Reviews route */}
            <Route path="/reviews" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <Reviews />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            {/* Partner routes */}
            {/* <Route path="/partners" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <PartnersSection />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } /> */}

            <Route path="/widgets" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <WidgetBuilder />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            <Route path="/site-editor" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <SiteEditor />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            {/* Workflow/Automation routes */}
            <Route path="/workflows" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <WorkflowCanvas />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            {/* Legacy redirect */}
            <Route path="/pipelines" element={<Navigate to="/workflows" replace />} />

            {/* Account Settings */}
            <Route path="/account/settings" element={
              <ProtectedRoute>
                <OnboardingGuard>
                  <AppLayout>
                    <AccountSettings />
                  </AppLayout>
                </OnboardingGuard>
              </ProtectedRoute>
            } />

            {/* New Business Settings Route */}
            <Route
              path="/business"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <AppLayout>
                      <BusinessSettings />
                    </AppLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />

            {/* Configuration routes - CORRECTED */}
            <Route path="/configuration" element={<Navigate to="/configuration/business" replace />} />
            <Route
              path="/configuration/business"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <AppLayout>
                      <BusinessSettings />
                    </AppLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/communications"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <AppLayout>
                      <CommunicationsEditor />
                    </AppLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/services"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <AppLayout>
                      <ServicesSection />
                    </AppLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            <Route
              path="/configuration/team"
              element={
                <ProtectedRoute>
                  <OnboardingGuard>
                    <AppLayout>
                      <TeamSection />
                    </AppLayout>
                  </OnboardingGuard>
                </ProtectedRoute>
              }
            />
            {/* CHANGED: Redirect old payment route to integrated business settings */}
            <Route 
              path="/configuration/payments" 
              element={<Navigate to="/configuration/business?section=payments" replace />} 
            />
            

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </SocketProvider>
      <Toaster />
    </Router>
  );
}

export default App;